/**
 * D09 — Payment method.
 *
 * Only rails the server has enabled are shown; the app carries no list of its
 * own. Selection is single-choice radio semantics — the desktop artwork showed
 * JazzCash and Easypaisa both ticked at once (gap G-23), which cannot happen
 * here.
 *
 * No card data is collected on this screen. Card rails hand off to the
 * provider's SDK and return a token, so a PAN never enters the app.
 */

import { useState } from 'react';
import { StyleSheet } from 'react-native';

import { formatMoney, normaliseWalletMsisdn } from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { Card } from '../../components/common/Card';
import { LockIcon } from '../../components/common/Icon';
import {
  PaymentAssurance,
  PaymentMethodPicker,
  paymentReady,
} from '../../components/checkout/PaymentMethodPicker';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useDonationStore } from '../donation/donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function PaymentScreen({ navigation }: DonorScreenProps<'Payment'>) {
  const t = useT();

  const quote = useDonationStore((s) => s.quote);
  const selected = useDonationStore((s) => s.paymentMethodCode);
  const setPaymentMethod = useDonationStore((s) => s.setPaymentMethod);
  const donor = useDonationStore((s) => s.donor);
  const walletMsisdn = useDonationStore((s) => s.walletMsisdn);
  const setWalletMsisdn = useDonationStore((s) => s.setWalletMsisdn);
  const [walletTouched, setWalletTouched] = useState(false);

  // Prefilled from the donor's own mobile when they gave one, and editable:
  // a family often pays from one person's wallet.
  const walletValue = walletMsisdn ?? donor?.mobileNumber ?? '';
  const canContinue = Boolean(quote) && paymentReady(selected, walletValue);

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-payment"
      subHeader={<CheckoutHeader current={3} title={t('Make Payment')} subtitle={t('Choose a payment method and complete your donation')} />}
      pinnedFooter={
        <Button
          label={quote ? `${t('Pay')} ${formatMoney(quote.totalAmount)}` : t('Continue')}
          onPress={() => {
            setWalletTouched(true);
            if (!canContinue) return;
            if (selected === 'easypaisa') setWalletMsisdn(normaliseWalletMsisdn(walletValue));
            navigation.navigate('Review');
          }}
          disabled={!canContinue}
          leadingIcon={<LockIcon />}
          testID="cta-pay"
        />
      }
    >
      {/* The amount, and nothing else. The meal, the headcount and the price
          per person were all settled two screens ago and are shown again on
          Review; what someone about to choose a payment method needs to see
          is what they are about to pay. */}
      {quote ? (
        <Card style={styles.total} testID="payment-total">
          <Text variant="label" tone="secondary">{t('Total Amount')}</Text>
          <Text variant="amount" tone="brand" testID="payment-total-amount">
            {formatMoney(quote.totalAmount)}
          </Text>
        </Card>
      ) : null}

      <PaymentMethodPicker
        selected={selected}
        onSelect={setPaymentMethod}
        walletValue={walletValue}
        onWalletChange={(value) => setWalletMsisdn(value)}
        walletTouched={walletTouched}
        onWalletBlur={() => setWalletTouched(true)}
        onContact={() => navigation.navigate('Contact')}
        emptyMessage="Online payment is not set up yet. Please try again later, or contact us to donate."
      />

      <PaymentAssurance />
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  total: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.lg,
  },
});
